import React, {useEffect} from "react";
import {Html5Qrcode} from "html5-qrcode";
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, Typography} from "@mui/material";
import {useLocalization} from "../i18n";
import {useFlag} from "@variocube/app-ui";

interface QrcodeScannerProps {
	open: boolean;
	onScanned: (value: string) => void;
	onClose: () => void;
}

const readerId = 'qrcode-reader';

export function QrcodeScanner({open, onScanned, onClose}: QrcodeScannerProps) {
	const {t} = useLocalization();

	const [ready, setReady] = useFlag(false);
	const [startError, setStartError, clearStartError] = useFlag(false);

	let scanner: Html5Qrcode;

	useEffect(() => {
		const interval = setInterval(() => {
			const readerElement = document.getElementById(readerId);
			if (readerElement !== null) {
				clearInterval(interval);

				if (open && scanner === undefined) {
					scanner = new Html5Qrcode(readerId);
					scanner
						.start(
							{ facingMode: 'environment', },
							{
								fps: 10,
								qrbox: {
									width: 250,
									height: 250,
								}
							},
							onScanned,
							err => {
								console.error('Failed to scan code', err)
							}
						)
						.then(() => {
							console.info('QR-Code Camera started.');
							clearStartError();
						})
						.catch(err => {
							console.error('Failed to start QR-Code Camera', err);
							setStartError();
						})
						.finally(setReady)
				}
			}
		}, 100)

		return () => {
			if (scanner !== undefined) {
				try {
					scanner.stop()
						.then(() => console.info('QR-Code Camera stopped.'));
				} catch (err) {
					console.error('Failed to stop QR-Code Camera', err);
				}
			}
		}
	}, [open])

	return (
		<Dialog
			open={open}
			keepMounted={true}
			onClose={onClose}
		>
			<DialogContent>
				<Box
					id={readerId}
					sx={theme => ({
						width: 400,
						[theme.breakpoints.down('xs')]: {
							width: 250
						}
					})}
				/>
				{!ready && <Typography>{t('location.scanStartHint')}</Typography>}
				{startError && <Alert severity="error">{t('location.scanStartError')}</Alert>}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>{t('actions.close')}</Button>
			</DialogActions>
		</Dialog>
	)
}
